import { TranslationsContext } from '@teliaee/telia-translate';
import { inject, observer } from 'mobx-react';
import * as React from 'react';
import {
  AnalyticsTrigger,
  Button,
  Card,
  CardContent,
  CardFooter,
  Grid,
  GridColumn,
  Notice,
  numToString,
  Select,
  Slider,
  stringToNum,
  Text,
  TextField,
  TSelectValue
} from 'telia-front-react';
import Config from '../Config';
import { SEGMENT_PRIVATE } from '../stores/SegmentStore';
import { getCustomerType } from '../util/SegmentUtil';
import { getSelectionValue } from './PostpayCalculatorUtil';
import { RentCalculatorProps } from './PostpayInterfaces';

const RentCalculator = (props: RentCalculatorProps) => {
  const { translate } = React.useContext(TranslationsContext);
  const {
    handleStateChange,
    state,
    maxProductPrice,
    maxContractFee,
    maxInterestRate,
    periodSlider,
    depositSelectInput,
    calculateMonthlyPayment,
    showBuyback,
    ssoStore,
    segmentStore
  } = props;

  const onProductPriceChange = (newValue: string): void => {
    let error: string | false = false;

    if (stringToNum(newValue) < 75) {
      error = translate('postpayCalculator.error.minProductPrice');
    }

    if (stringToNum(newValue) > maxProductPrice) {
      error = translate('postpayCalculator.error.maxProductPrice', { amount: maxProductPrice });
    }

    if (isNaN(stringToNum(newValue))) {
      error = translate('postpayCalculator.error.insertNumber');
    }

    handleStateChange({
      productPrice: newValue,
      productPriceError: error,
      contractFee: !error ? getContractFeeForPriceChange(stringToNum(newValue)) : state.contractFee,
      contractFeeError: !error ? false : state.contractFeeError
    });
  };

  const getContractFeeForPriceChange = (price: number): string => {
    if (ssoStore.isEmployee) {
      return '9,9';
    }
    return price >= 75 && price < 150 ? '9,9' : price >= 150 && price < 200 ? '15,9' : '19,9';
  };

  const onContractFeeChange = (newValue: string): void => {
    let error: string | false = false;

    if (stringToNum(newValue) > maxContractFee) {
      error = translate('postpayCalculator.error.maxContractFee', { amount: maxContractFee });
    }

    if (isNaN(stringToNum(newValue))) {
      error = translate('postpayCalculator.error.insertNumber');
    }

    handleStateChange({
      contractFee: newValue,
      contractFeeError: error
    });
  };

  const onInterestRateChange = (newValue: string): void => {
    let error: string | false = false;

    if (stringToNum(newValue) > maxInterestRate) {
      error = translate('postpayCalculator.error.maxInterestRate', { amount: maxInterestRate });
    }

    if (isNaN(stringToNum(newValue))) {
      error = translate('postpayCalculator.error.insertNumber');
    }

    handleStateChange({
      interestRate: newValue,
      interestRateError: error
    });
  };

  const onPeriodSliderChange = (newValue: number[] | number): void =>
    handleStateChange({
      period: newValue
    });

  const onDepositSelectChange = (newValue: TSelectValue): void =>
    handleStateChange({
      depositSelection: newValue,
      depositSelectionValue: getSelectionValue(newValue)
    });

  const calculateDepositAmount = (): number => {
    const price: number = stringToNum(state.productPrice);
    const percentage: number = stringToNum(state.depositSelectionValue);

    return (price * percentage) / 100;
  };

  const calculateBuyBackPercentAsDecimal = (period: number): number => {
    if (period <= 12) {
      return 0.4;
    } else if (period <= 24) {
      return 0.2;
    } else if (period <= 36) {
      return 0.1;
    } else {
      return 0.05;
    }
  };

  const { contractFeeError, interestRateError, productPriceError, productPrice, period, depositSelection, contractFee, interestRate } = state;
  const hasError = (contractFeeError || interestRateError || productPriceError) && true;
  const depositAmount = !hasError ? calculateDepositAmount() : 0;
  const buybackAmount = stringToNum(productPrice) * calculateBuyBackPercentAsDecimal(Number(period));
  const monthlyPayment = !hasError
    ? calculateMonthlyPayment({ productPrice, period, interestRate, contractFee, depositAmount, buybackAmount: buybackAmount })
    : 0;

  const renderCheckLimits = () => {
    const customerType = getCustomerType(segmentStore.currentSegment);

    if (ssoStore.loggedIn) {
      return (
        <AnalyticsTrigger trigger={{ callback: 'onClick', id: 'Link - Järelmaksu limiit kalkulaatoris' }}>
          <Button
            text={translate(`postpayCalculator.${customerType}.checkLimits`)}
            url={`${Config.teliaShopUrl}${translate('postpayCalculator.checkLimits.path')}`}
            link={true}
            iconRight="#arrow-right"
            target="_blank"
          />
        </AnalyticsTrigger>
      );
    }
    return <Notice size="small">{translate(`postpayCalculator.${customerType}.checkLimits.logIn`)}</Notice>;
  };
  return (
    <Card padding="medium" background="neutral-bg-level-1" type={segmentStore.currentSegment === SEGMENT_PRIVATE ? 'light' : undefined}>
      <CardContent>
        <Text>
          {renderCheckLimits()}
          <Grid>
            <GridColumn width={['sm-6']}>
              <TextField
                id="product-price"
                label={translate('postpayCalculator.productPrice')}
                onChange={onProductPriceChange}
                value={productPrice}
                {...(productPriceError && {
                  isInvalid: true,
                  helper: { text: productPriceError, type: 'error' }
                })}
              />
            </GridColumn>
            <GridColumn width={['sm-6']}>
              <p className="text-tiny">{translate('postpayCalculator.period')}</p>
              <Slider
                {...periodSlider}
                id="period-slider"
                step={periodSlider?.step || 6}
                tooltips={true}
                unit={translate('postpayCalculator.period.unit')}
                start={period}
                onChange={onPeriodSliderChange}
              />
            </GridColumn>
          </Grid>
          <Grid>
            <GridColumn width={['sm-4']}>
              <Select
                {...depositSelectInput}
                id="deposit"
                label={translate('postpayCalculator.deposit')}
                onChange={onDepositSelectChange}
                value={depositSelection}
              />
            </GridColumn>
            <GridColumn width={['sm-4']}>
              <TextField
                id="contract-fee"
                label={translate('postpayCalculator.contractFee')}
                value={contractFee}
                onChange={onContractFeeChange}
                {...(contractFeeError && {
                  isInvalid: true,
                  helper: { text: contractFeeError, type: 'error' }
                })}
              />
            </GridColumn>
            <GridColumn width={['sm-4']}>
              <TextField
                id="interest"
                label={translate('postpayCalculator.interest')}
                value={interestRate}
                onChange={onInterestRateChange}
                {...(interestRateError && {
                  isInvalid: true,
                  helper: { text: interestRateError, type: 'error' }
                })}
              />
            </GridColumn>
          </Grid>
          <Notice size="small">{translate('postpayCalculator.additionalInformation')}</Notice>
        </Text>
      </CardContent>
      <CardFooter>
        <Grid>
          <GridColumn width={['sm-4']}>
            <p>{translate('postpayCalculator.deposit')}:</p>
            <p className="h4 text-bold">{`${hasError ? '–' : numToString(depositAmount, 2)} €`}</p>
          </GridColumn>
          <GridColumn width={['sm-4']}>
            <p>{translate('postpayCalculator.monthlyFee')}</p>
            <p className="h4 text-bold">{`${period} x ${hasError ? '–' : `${numToString(monthlyPayment, 2)}`} €`}</p>
          </GridColumn>
          {showBuyback && (
            <GridColumn width={['sm-4']}>
              <p>{translate('postpayCalculator.buyback')}</p>
              <p className="h4 text-bold">{`${hasError ? '–' : Math.floor(buybackAmount)} €`}</p>
            </GridColumn>
          )}
        </Grid>
      </CardFooter>
    </Card>
  );
};

export default inject('ssoStore', 'segmentStore')(observer(RentCalculator));
